import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const useKeyboardShortcuts = (options = {}) => {
  const navigate = useNavigate();
  const { 
    onNewTicket, 
    onSearch, 
    onRefresh,
    onToggleView,
    onToggleCompact,
    enabled = true 
  } = options;

  const handleKeyDown = useCallback((event) => {
    // Ignorer si on est dans un input, textarea ou contenteditable
    const target = event.target;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable
    ) {
      // Exception: Escape fonctionne toujours
      if (event.key !== 'Escape') return;
    }

    // Ignorer si Ctrl/Cmd + autre touche (sauf nos raccourcis)
    const hasModifier = event.ctrlKey || event.metaKey;

    switch (event.key.toLowerCase()) {
      // N = Nouveau ticket
      case 'n':
        if (!hasModifier && onNewTicket) {
          event.preventDefault();
          onNewTicket();
        }
        break;

      // / ou Ctrl+K = Recherche
      case '/':
        if (!hasModifier && onSearch) {
          event.preventDefault();
          onSearch();
        }
        break;
      case 'k':
        if (hasModifier && onSearch) {
          event.preventDefault();
          onSearch();
        }
        break;

      // R = Rafraîchir
      case 'r':
        if (!hasModifier && onRefresh) {
          event.preventDefault();
          onRefresh();
        }
        break;

      // V = Changer de vue (liste/kanban)
      case 'v':
        if (!hasModifier && onToggleView) {
          event.preventDefault();
          onToggleView();
        }
        break;

      // C = Mode compact
      case 'c':
        if (!hasModifier && onToggleCompact) {
          event.preventDefault();
          onToggleCompact();
        }
        break;

      // G puis T = Go to Tickets
      case 'g':
        if (!hasModifier) {
          // Attendre la prochaine touche
          const handleNextKey = (e) => {
            document.removeEventListener('keydown', handleNextKey);
            if (e.key.toLowerCase() === 't') {
              e.preventDefault();
              navigate('/tickets');
            } else if (e.key.toLowerCase() === 'd') {
              e.preventDefault();
              navigate('/dashboard');
            } else if (e.key.toLowerCase() === 's') {
              e.preventDefault();
              navigate('/settings');
            } else if (e.key.toLowerCase() === 'k') {
              e.preventDefault();
              navigate('/kb');
            }
          };
          setTimeout(() => {
            document.addEventListener('keydown', handleNextKey, { once: true });
            // Timeout pour annuler si pas de deuxième touche
            setTimeout(() => {
              document.removeEventListener('keydown', handleNextKey);
            }, 1000);
          }, 0);
        }
        break;

      // Escape = Fermer modal/menu
      case 'escape':
        // Géré par les composants individuels
        break;

      // ? = Aide raccourcis
      case '?':
        if (event.shiftKey) {
          event.preventDefault();
          // Dispatch custom event pour afficher l'aide
          window.dispatchEvent(new CustomEvent('show-shortcuts-help'));
        }
        break;

      default:
        break;
    }
  }, [navigate, onNewTicket, onSearch, onRefresh, onToggleView, onToggleCompact]);

  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, enabled]);

  return null;
};

// Composant d'aide pour les raccourcis
export const ShortcutsHelp = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const shortcuts = [
    { key: 'N', description: 'Nouveau ticket' },
    { key: '/', description: 'Rechercher' },
    { key: 'Ctrl+K', description: 'Rechercher' },
    { key: 'R', description: 'Rafraîchir' },
    { key: 'V', description: 'Changer de vue' },
    { key: 'C', description: 'Mode compact' },
    { key: 'G puis T', description: 'Aller aux tickets' },
    { key: 'G puis D', description: 'Aller au dashboard' },
    { key: 'G puis S', description: 'Aller aux paramètres' },
    { key: 'G puis K', description: 'Base de connaissances' },
    { key: 'Escape', description: 'Fermer' },
    { key: '?', description: 'Afficher cette aide' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          ⌨️ Raccourcis clavier
        </h2>
        <div className="space-y-2">
          {shortcuts.map(({ key, description }) => (
            <div key={key} className="flex items-center justify-between py-1">
              <span className="text-gray-600 dark:text-gray-400">{description}</span>
              <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm font-mono text-gray-800 dark:text-gray-200">
                {key}
              </kbd>
            </div>
          ))}
        </div>
        <button 
          onClick={onClose}
          className="mt-4 w-full btn-secondary"
        >
          Fermer
        </button>
      </div>
    </div>
  );
};

export default useKeyboardShortcuts;
